import { sgSupabase } from "../../lib/sgSupabaseClient";
import api from "../../utils/api";
import { assertCanUseSG } from "./access";
import {
  toUsuarioPayload,
  toAddressPayload,
  hasAddressData,
} from "./schemas";

export interface V5MainUser {
  email?: string;
  username?: string;
  password?: string;
}

export async function notifyV5CompanySync(
  sgUserId: string | number,
  origin: string[],
  mainUser?: V5MainUser,
  applyStatus: boolean = true
) {
  // Se llama siempre: el backend decide (crear/actualizar si origin ∋ v5, o
  // desvincular si la fila estaba ligada y ya no tiene v5). mainUser solo aplica
  // a la creación con origen v5.
  const hasV5 = Array.isArray(origin) && origin.includes("v5");
  const hasMainUser = !!(
    hasV5 &&
    mainUser &&
    mainUser.email &&
    mainUser.username &&
    mainUser.password
  );
  try {
    await api.post("/sg-sync/company-from-sg", {
      sgUserId,
      applyStatus,
      ...(hasMainUser && { mainUser }),
    });
  } catch (err) {
    console.warn("[sgSync] notify V5 falló:", err);
  }
}

export interface SgUserRow {
  id: number;
  user_name: string | null;
  first_name: string | null;
  last_name: string | null;
  status_name: string | null;
  business_name: string | null;
  origin: string[] | null;
  activos: number;
  inactivos: number;
  facturables: number;
  total: number;
  total_count: number;
}

export interface SgUsersPage {
  rows: SgUserRow[];
  total: number;
}

export const SG_USERS_PER_PAGE = 30;

export async function fetchSgUsers(
  page: number,
  search: string,
  perPage: number = SG_USERS_PER_PAGE
): Promise<SgUsersPage> {
  const { data, error } = await sgSupabase.rpc("list_users", {
    p_search: search.trim(),
    p_limit: perPage,
    p_offset: page * perPage,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as SgUserRow[];
  return { rows, total: rows[0]?.total_count ?? 0 };
}

export async function fetchAllSgUsers(search: string): Promise<SgUserRow[]> {
  const BATCH = 1000;
  const all: SgUserRow[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await sgSupabase.rpc("list_users", {
      p_search: search.trim(),
      p_limit: BATCH,
      p_offset: offset,
    });
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as SgUserRow[];
    all.push(...rows);
    if (rows.length < BATCH) break;
    offset += BATCH;
  }
  return all;
}

// IMEIs por usuario de SG (todas sus unidades), para su origen calculado. Con
// userIds acota a esos usuarios; sin ellos trae todo (~27k equipos, export).
// Paginado: PostgREST corta en 1000 filas y hay usuarios con más unidades.
export async function fetchImeisByUser(
  userIds?: number[]
): Promise<Record<number, string[]>> {
  const BATCH = 1000;
  const out: Record<number, string[]> = {};
  if (userIds && !userIds.length) return out;
  for (let from = 0; ; from += BATCH) {
    let q = sgSupabase
      .from("devices")
      .select("id, user_id, imei")
      .order("id", { ascending: true })
      .range(from, from + BATCH - 1);
    if (userIds) q = q.in("user_id", userIds);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    for (const d of data ?? []) {
      if (d.imei) (out[d.user_id] ??= []).push(d.imei);
    }
    if (!data || data.length < BATCH) break;
  }
  return out;
}

export async function fetchSgAccountConditions() {
  const { data, error } = await sgSupabase
    .from("account_conditions")
    .select("id, name")
    .order("name");
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function fetchUsuarioLookups() {
  const [statusRes, conditionRes, ctypeRes] = await Promise.all([
    sgSupabase.from("user_statuses").select("id, name").order("name"),
    sgSupabase
      .from("account_conditions")
      .select("id, name, user_status_id")
      .order("name"),
    sgSupabase.from("contact_types").select("id, name").order("name"),
  ]);
  return {
    statuses: statusRes.data ?? [],
    conditions: conditionRes.data ?? [],
    contactTypes: ctypeRes.data ?? [],
  };
}

export async function fetchUsuario(id: string | number) {
  const [u, b, c, d, ac] = await Promise.all([
    sgSupabase.from("users").select("*").eq("id", id).single(),
    sgSupabase.from("billings").select("*").eq("user_id", id).maybeSingle(),
    sgSupabase.from("contacts").select("*").eq("user_id", id).maybeSingle(),
    sgSupabase
      .from("devices")
      .select(
        "id, name, uniq_id, imei, phone, last_time_connected, statuses(name_es, name), brands(brand, model)"
      )
      .eq("user_id", id)
      .order("last_time_connected", { ascending: false }),
    sgSupabase
      .from("account_contacts")
      .select("id, name, contact_type_id, contact_types(name)")
      .eq("user_id", id)
      .order("id"),
  ]);
  if (u.error) throw new Error(u.error.message);
  return {
    user: u.data,
    billing: b.data,
    contact: c.data,
    devices: d.data ?? [],
    accountContacts: ac.data ?? [],
  };
}

async function upsertAddress(
  table: string,
  addr: any,
  userId: string | number,
  now: string
) {
  if (addr.id) {
    const { error } = await sgSupabase
      .from(table)
      .update({ ...toAddressPayload(addr), updated_at: now })
      .eq("id", addr.id);
    if (error) throw new Error(error.message);
  } else if (hasAddressData(addr)) {
    const { error } = await sgSupabase
      .from(table)
      .insert({ ...toAddressPayload(addr), user_id: userId });
    if (error) throw new Error(error.message);
  }
}

export async function saveUsuario({
  id,
  isNew,
  values,
  billingStatusChanged = true,
}: {
  id?: string | number;
  isNew: boolean;
  values: any;
  billingStatusChanged?: boolean;
}) {
  assertCanUseSG();
  const payload = toUsuarioPayload(values);
  if (payload.origin.length === 0) {
    throw new Error("Selecciona al menos un origen (V5, Wialon o Mapon).");
  }
  const now = new Date().toISOString();

  let userId = id;
  if (isNew) {
    const { data, error } = await sgSupabase
      .from("users")
      .insert({ ...payload, updated_at: now })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    userId = data.id;
  } else {
    const { error } = await sgSupabase
      .from("users")
      .update({ ...payload, updated_at: now })
      .eq("id", userId);
    if (error) throw new Error(error.message);
  }

  await upsertAddress("billings", values.billing, userId!, now);
  await upsertAddress("contacts", values.contact, userId!, now);
  await notifyV5CompanySync(
    userId!,
    payload.origin,
    {
      email: values.v5_email,
      username: values.v5_username,
      password: values.v5_password,
    },
    isNew || billingStatusChanged
  );
  return userId;
}

export async function createAccountContact(userId: string | number) {
  assertCanUseSG();
  const { data, error } = await sgSupabase
    .from("account_contacts")
    .insert({ user_id: userId, name: "", contact_type_id: null })
    .select("id, name, contact_type_id, contact_types(name)")
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function saveAccountContact(c: any) {
  assertCanUseSG();
  const { error } = await sgSupabase
    .from("account_contacts")
    .update({ name: c.name || null, contact_type_id: c.contact_type_id || null })
    .eq("id", c.id);
  if (error) throw new Error(error.message);
}

export async function deleteAccountContact(id: string | number) {
  assertCanUseSG();
  const { error } = await sgSupabase
    .from("account_contacts")
    .delete()
    .eq("id", id);
  if (error) throw new Error(error.message);
}

function isV5LinkedUser(u: any): boolean {
  return !!(
    u?.origin_ids?.v5 ||
    (Array.isArray(u?.origin) && u.origin.includes("v5"))
  );
}

export async function fetchUserOption(id: string | number) {
  const { data } = await sgSupabase
    .from("users")
    .select("id, user_name, origin, origin_ids")
    .eq("id", id)
    .maybeSingle();
  return data
    ? { value: data.id, label: data.user_name, v5: isV5LinkedUser(data) }
    : { value: id, label: `#${id}`, v5: false };
}

export async function notifyV5CompanyDelete(sgUserId: string | number) {
  try {
    await api.post("/sg-sync/company-delete-from-sg", { sgUserId });
  } catch (err) {
    console.warn("[sgSync] notify V5 delete falló:", err);
  }
}

export async function deleteUsuario(id: string | number) {
  assertCanUseSG();
  await notifyV5CompanyDelete(id);
  const { error } = await sgSupabase.from("users").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

export async function searchUsers(input: string) {
  let q = sgSupabase
    .from("users")
    .select("id, user_name, origin, origin_ids")
    .order("user_name")
    .limit(25);
  const term = input.trim().replace(/[,()*]/g, " ");
  if (term) q = q.ilike("user_name", `%${term}%`);
  const { data } = await q;
  return (data ?? []).map((u: any) => ({
    value: u.id,
    label: u.user_name,
    v5: isV5LinkedUser(u),
  }));
}
